import { FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { feedService } from '@/services/feed.service';
import FollowButton from '@/components/FollowButton';
import { FeedItem, FeedItemType, FeedTab } from '@/types/feed';

const FEED_CONFIG: Record<FeedItemType, { icon: string; color: string; bgColor: string }> = {
  COURSE_COMPLETED: {
    icon: 'school',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  BADGE_EARNED: {
    icon: 'emoji_events',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  LEVEL_UP: {
    icon: 'trending_up',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
  },
  MISSION_COMPLETED: {
    icon: 'flag',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
};

const TABS: { key: FeedTab; label: string; icon: string }[] = [
  { key: 'personal', label: 'Meu Feed', icon: 'person' },
  { key: 'sector', label: 'Meu Setor', icon: 'corporate_fare' },
  { key: 'social', label: 'Seguindo', icon: 'group' },
];

const PAGE_SIZE = 20;

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'agora mesmo';
  if (diffMinutes < 60) return `há ${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minutos'}`;
  if (diffHours < 24) return `há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
  if (diffDays < 30) return `há ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;

  return date.toLocaleDateString('pt-BR');
}

function getDestinationUrl(item: FeedItem): string | null {
  const metadata = item.metadata;
  switch (item.type) {
    case 'COURSE_COMPLETED':
      if (metadata.course_id) return `/trilhas/${metadata.course_id}`;
      if (metadata.trail_id) return `/trilhas/${metadata.trail_id}`;
      return '/trilhas';
    case 'BADGE_EARNED':
      return '/perfil';
    case 'LEVEL_UP':
      return '/perfil';
    case 'MISSION_COMPLETED':
      return '/perfil';
    default:
      return null;
  }
}

const FeedCard: FC<{
  item: FeedItem;
  onClick: (url: string) => void;
  onUserClick: (userId: string) => void;
  showFollowButton?: boolean;
  currentUserId?: string;
}> = ({ item, onClick, onUserClick, showFollowButton = false, currentUserId }) => {
  const config = FEED_CONFIG[item.type];
  const destinationUrl = getDestinationUrl(item);
  const isOwnItem = currentUserId === item.user.id;

  const handleClick = () => {
    if (destinationUrl) {
      onClick(destinationUrl);
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUserClick(item.user.id);
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-surface-secondary border border-theme-border rounded-xl p-4 flex items-start gap-4 transition-all hover:bg-surface-hover ${
        destinationUrl ? 'cursor-pointer' : ''
      }`}
      role="article"
      data-testid={`feed-card-${item.type}`}
    >
      {/* User Avatar */}
      <div
        className="shrink-0 cursor-pointer"
        onClick={handleUserClick}
        data-testid={`feed-user-avatar-${item.id}`}
      >
        {item.user.avatar ? (
          <img
            src={item.user.avatar}
            alt={item.user.name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
            <span className="text-accent font-bold text-sm">
              {item.user.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="font-semibold text-content-primary text-sm truncate cursor-pointer hover:underline"
            onClick={handleUserClick}
            data-testid={`feed-user-name-${item.id}`}
          >
            {item.user.name}
          </span>
          <span className="text-content-tertiary text-xs">
            Nv. {item.user.level}
          </span>
          {showFollowButton && !isOwnItem && (
            <FollowButton userId={item.user.id} />
          )}
        </div>
        <p className="text-content-secondary text-sm leading-relaxed">
          {item.message}
        </p>
        <span className="text-content-tertiary text-xs mt-1 block">
          {formatRelativeTime(item.created_at)}
        </span>
      </div>

      {/* Type Icon */}
      <div className={`shrink-0 p-2 rounded-lg ${config.bgColor}`}>
        <span className={`material-symbols-outlined text-lg ${config.color}`}>
          {config.icon}
        </span>
      </div>
    </div>
  );
};

const FeedPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState<FeedTab>('personal');
  const [items, setItems] = useState<FeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchFeed = useCallback(async (tab: FeedTab, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const currentCount = append ? items.length : 0;
      const limit = currentCount + PAGE_SIZE;

      let data: FeedItem[];
      switch (tab) {
        case 'personal':
          data = await feedService.getPersonalFeed(limit);
          break;
        case 'sector':
          data = await feedService.getSectorFeed(limit);
          break;
        case 'social':
          data = await feedService.getSocialFeed(limit);
          break;
      }

      setItems(data);
      setHasMore(data.length >= limit);
    } catch (error) {
      console.error('Erro ao carregar feed:', error);
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [items.length]);

  useEffect(() => {
    fetchFeed(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabChange = (tab: FeedTab) => {
    setActiveTab(tab);
    setItems([]);
    setHasMore(false);
  };

  const handleLoadMore = () => {
    fetchFeed(activeTab, true);
  };

  const handleCardClick = (url: string) => {
    navigate(url);
  };

  const handleUserClick = (userId: string) => {
    if (userId === user?.id) {
      navigate('/perfil');
    } else {
      navigate('/perfil');
    }
  };

  const showFollowButton = activeTab === 'sector';

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-content-primary">Atividades</h1>
        <p className="text-content-secondary text-sm mt-1">
          Acompanhe as atividades da plataforma
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-surface-secondary p-1 rounded-xl mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
              activeTab === tab.key
                ? 'bg-accent text-accent-contrast'
                : 'text-content-secondary hover:text-content-primary'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <span className="material-symbols-outlined text-lg">{tab.icon}</span>
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-surface-secondary border border-theme-border rounded-xl p-4 animate-pulse"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-surface-hover" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-surface-hover rounded w-1/3" />
                  <div className="h-3 bg-surface-hover rounded w-2/3" />
                  <div className="h-3 bg-surface-hover rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Feed Items */}
      {!isLoading && items.length > 0 && (
        <div className="space-y-3">
          {items.map((item) => (
            <FeedCard
              key={item.id}
              item={item}
              onClick={handleCardClick}
              onUserClick={handleUserClick}
              showFollowButton={showFollowButton}
              currentUserId={user?.id}
            />
          ))}
        </div>
      )}

      {/* Load More */}
      {!isLoading && hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="flex items-center gap-2 px-6 py-3 bg-surface-secondary border border-theme-border text-content-primary rounded-xl font-medium hover:bg-surface-hover transition-all disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                Carregando...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">expand_more</span>
                Carregar mais
              </>
            )}
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && items.length === 0 && (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-6xl text-content-tertiary mb-4 block">
            dynamic_feed
          </span>
          <h3 className="text-lg font-semibold text-content-primary mb-2">
            Nenhuma atividade recente
          </h3>
          <p className="text-content-secondary text-sm max-w-md mx-auto">
            {activeTab === 'personal'
              ? 'Você ainda não tem atividades. Completar a primeira aula já gera uma!'
              : activeTab === 'sector'
                ? 'Ninguém do seu setor fez atividade recente. Que tal ser o primeiro?'
                : 'Você ainda não segue ninguém. Visite o perfil de colegas pra começar a seguir.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default FeedPage;
